import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApp } from "app/contexts/AppContext";
import {
  clearJobDetail,
  fetchJobDetail,
  setupSendJob,
} from "app/containers/Jobs/slice";
import { fetchConnectAccount } from "app/containers/PaymentConnection/slice";
import { fetchContracts } from "app/containers/ContractManagement/slice";
import {
  fetchEmailTemplates,
  fetchProviders,
} from "app/containers/EmailAutomation/slice";
import { getContractEmailWarning } from "app/containers/JobCalendar/utils/sendTrackRules";
import { PC_STATE } from "app/containers/PaymentConnection/constants";
import {
  toSendOutcome,
  failedSendOutcome,
  pickTrackStates,
} from "app/containers/Jobs/utils/setupSend";

const NO_TRACK_STATES = { contractState: null, paymentState: null };

/**
 * Everything JobSetupModal needs to offer the Contract / Payment Request
 * "Save & Send" checkboxes, shared by Job Detail and the calendar.
 *
 * Account-level data (Connect account, contract templates) is fetched only
 * while `active`, so a page that mounts a closed modal costs no requests.
 * Pass `fetchDetail` when the caller has no contract status of its own: the
 * calendar API's contract domain is always null, so only GET /jobs/{id}/ can
 * say whether a contract was already sent.
 *
 * @param {{jobId: string|null, active?: boolean, fetchDetail?: boolean}} args
 */
export const useJobSetupSend = ({
  jobId,
  active = false,
  fetchDetail = false,
}) => {
  const dispatch = useDispatch();
  const { user } = useApp();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const detail = useSelector((state) => state.jobs.detail);
  const detailStatus = useSelector((state) => state.jobs.getJobDetailStatus);
  const currentDetailRequestId = useSelector(
    (state) => state.jobs.currentDetailRequestId
  );
  const pcState = useSelector((state) => state.paymentConnection.derivedState);
  const contractTemplates = useSelector(
    (state) => state.contractManagement.templates
  );
  const emailConnected = useSelector(
    (state) => state.emailAutomation.isConnected
  );
  const providersStatus = useSelector(
    (state) => state.emailAutomation.fetchStatus
  );
  const emailTemplates = useSelector(
    (state) => state.emailAutomation.templates
  );
  const templatesStatus = useSelector(
    (state) => state.emailAutomation.templatesFetchStatus
  );

  // A missing subscription reads as Free everywhere else (UserMenu,
  // ContractManagement). Defaulting to a paid tier is worse than cosmetic: it
  // decides which sends are offered, so a Free inspector would get live
  // checkboxes until the profile lands.
  const tier = user?.profile?.subscription?.tier || "Free";
  const stripeConnected = pcState === PC_STATE.CONNECTED;
  // LOADING is the initial value, so "not CONNECTED" is not yet "not connected".
  const stripeLoading = pcState === PC_STATE.LOADING;

  useEffect(() => {
    if (!active) return;
    dispatch(fetchConnectAccount());
    dispatch(fetchContracts());
    dispatch(fetchProviders());
    dispatch(fetchEmailTemplates());
  }, [dispatch, active]);

  // "idle" / "loading" mean not answered yet. A failed fetch fails open: the
  // backend still records the skipped email for Copy Content, and blocking
  // every contract on a providers outage would be worse than that.
  const contractEmailLoading =
    providersStatus === "idle" || providersStatus === "loading";
  const contractEmailConnected =
    providersStatus !== "success" || emailConnected;

  // Template state only; a missing mailbox blocks instead (see above).
  const contractEmailWarning = useMemo(() => {
    if (!emailConnected || templatesStatus !== "success") return null;
    return getContractEmailWarning({
      contractEmailTemplate: emailTemplates.find(
        (template) => template.event_type === "contract_sent"
      ),
    });
  }, [emailConnected, templatesStatus, emailTemplates]);

  // Only the detail fetch dispatched for THIS open is trusted. On a reopen the
  // previous fetch's result still matches the job id for a render, which
  // would briefly auto-check a track off stale status.
  const [ownRequestId, setOwnRequestId] = useState(null);
  const loadTrackStates = useCallback(() => {
    setOwnRequestId(dispatch(fetchJobDetail(jobId)).requestId);
  }, [dispatch, jobId]);
  useEffect(() => {
    if (!(active && fetchDetail && jobId)) {
      setOwnRequestId(null);
      return undefined;
    }
    loadTrackStates();
    // The calendar shares the jobs slice with Job Detail: a detail or error
    // left behind here would flash on the next /jobs/<id> visit.
    return () => dispatch(clearJobDetail());
  }, [active, fetchDetail, jobId, loadTrackStates, dispatch]);

  const openUpgrade = useCallback(() => setShowUpgrade(true), []);
  const closeUpgrade = useCallback(() => setShowUpgrade(false), []);

  // One composite setup-send call; the backend runs the contract and payment
  // tracks independently and reports each one's {ok} back. Each track's
  // user_error_msg is carried through: only the backend's copy tells the
  // inspector whether a BoldSign failure is retryable, account-level or a
  // rejected payload.
  const onSendTracks = useCallback(
    async ({ sendContract, sendPayment, contractTemplateId }) => {
      const requested = { sendContract, sendPayment };
      try {
        const res = await dispatch(
          setupSendJob({
            id: jobId,
            sendContract,
            sendPaymentRequest: sendPayment,
            contractTemplateId,
          })
        ).unwrap();
        return toSendOutcome(res, requested);
      } catch {
        // The request itself failed (network, 404), so there is no per-track
        // user_error_msg. The thunk's generic message would only replace the
        // track-specific fallback copy, so it is deliberately not passed on.
        return failedSendOutcome(requested);
      }
    },
    [dispatch, jobId]
  );

  const ownFetchIsCurrent =
    !fetchDetail ||
    (Boolean(ownRequestId) && ownRequestId === currentDetailRequestId);
  const trackStates = useMemo(
    () =>
      ownFetchIsCurrent
        ? pickTrackStates({ detail, jobId, status: detailStatus })
        : NO_TRACK_STATES,
    [ownFetchIsCurrent, detail, jobId, detailStatus]
  );
  // A failed detail fetch leaves both tracks null, which Setup would read as
  // "nothing to send". Flag it so the modal can offer a retry instead.
  const trackStatesFailed =
    fetchDetail && ownFetchIsCurrent && detailStatus === "failed";

  const setupModalProps = useMemo(
    () => ({
      ...trackStates,
      trackStatesFailed,
      onRetryTrackStates: fetchDetail ? loadTrackStates : undefined,
      tier,
      stripeConnected,
      stripeLoading,
      contractTemplates,
      contractEmailConnected,
      contractEmailLoading,
      contractEmailWarning,
      onRequestUpgrade: openUpgrade,
      onSendTracks,
    }),
    [
      trackStates,
      trackStatesFailed,
      fetchDetail,
      loadTrackStates,
      tier,
      stripeConnected,
      stripeLoading,
      contractTemplates,
      contractEmailConnected,
      contractEmailLoading,
      contractEmailWarning,
      openUpgrade,
      onSendTracks,
    ]
  );

  return {
    tier,
    stripeConnected,
    stripeLoading,
    contractTemplates,
    showUpgrade,
    openUpgrade,
    closeUpgrade,
    setupModalProps,
  };
};
